import { useState } from "react";
import { FullDataExportModel } from "./FullDataExportDialog.types";
import { post } from "@common/api/apiShared.ts";
import { IFullDataExportDialogProps } from "./FullDataExportDialog";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

function useFullDataExportDialogHooks(props: IFullDataExportDialogProps) {
    const snackbar = useDotLegalSnackbar();
    const { translateString } = useTranslation();

    const [exportModel, setExportModel] = useState<FullDataExportModel>(getAllProperties(false));

    const saveMutation = useDotLegalMutation(save, {
        onSuccess: () => {
            props.onDialogClose();
            snackbar.show(translateString("fullDataExportInProgress"), "success", true);
        },
    });

    const triggerExport = async () => {
        await saveMutation.mutateAsync(exportModel);
    };

    function save(model: FullDataExportModel) {
        return post("/full-data-export/export", model);
    }

    const modelUpdater = {
        onProcessingActivitiesChange: (checked: boolean) => {
            setExportModel({ ...exportModel, processingActivities: checked });
        },
        onSystemsChange: (checked: boolean) => {
            setExportModel({ ...exportModel, systems: checked });
        },
        onLegalEntitiesChange: (checked: boolean) => {
            setExportModel({ ...exportModel, legalEntities: checked });
        },
        onContractsChange: (checked: boolean) => {
            setExportModel({ ...exportModel, contracts: checked });
        },
        onPoliciesChange: (checked: boolean) => {
            setExportModel({ ...exportModel, policies: checked });
        },
        onDocumentsChange(checked: boolean) {
            setExportModel({ ...exportModel, documents: checked });
        },
        toggleAll: () => {
            const newValue = !allSelected;
            setExportModel(getAllProperties(newValue));
        },
    };

    const allSelected = Object.values(exportModel).every((value) => value);
    const exportButtonEnabled = allSelected || Object.values(exportModel).some((value) => value);

    return {
        exportModel,
        modelUpdater,
        allSelected,
        triggerExport,
        exportButtonEnabled,
        inProgress: saveMutation.isLoading,
    };

    function getAllProperties(checked: boolean) {
        return {
            processingActivities: checked,
            systems: checked,
            legalEntities: checked,
            contracts: checked,
            policies: checked,
            documents: checked,
        };
    }
}

export default useFullDataExportDialogHooks;
